import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import { countryList } from '@nxsys/utils';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppSelect from '@nxsys/components/AppSelect';
import { Form, Row, Col } from 'antd';
import { useImperativeHandle, Ref, useEffect, useState } from 'react';
import { Option } from '@nxsys/types/models/Common';

type RTISettingsFormRef = {
  submit: () => void;
};

export type RTISettingsFormValues = {
  enableRTI: boolean;
  userId: string;
  password: string;
  agentForename: string;
  agentSurname: string;
  agentAddress1: string;
  agentAddress2: string;
  agentCity: string;
  agentCounty: string;
  agentPostCode: string;
  agentCountry: Option<string> | null;
};

type RTISettingsFormProps = {
  companyData?: ICompany;
  formRef?: Ref<RTISettingsFormRef>;
  onSubmit?: (values: RTISettingsFormValues) => void;
};

const RTISettingsForm: React.FC<RTISettingsFormProps> = ({
  companyData,
  formRef,
  onSubmit,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  const onFinish = (values: any) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  const enableRTI = Form.useWatch('enableRTI', form);

  useEffect(() => {
    if (companyData?.rtiSetting && needReinitializeForm) {
      const initialValues = companyData?.rtiSetting
        ? {
            enableRTI: companyData.rtiSetting.enableRTI || false,
            userId: companyData.rtiSetting.userId || '',
            password: companyData.rtiSetting.password,
            agentForename: companyData.rtiSetting.agentForename || '',
            agentSurname: companyData.rtiSetting.agentSurname || '',
            agentAddress1: companyData.rtiSetting.agentAddress?.[0] || '',
            agentAddress2: companyData.rtiSetting.agentAddress?.[1] || '',
            agentCity: companyData.rtiSetting.agentCity || '',
            agentCounty: companyData.rtiSetting.agentCounty || '',
            agentPostCode: companyData.rtiSetting.agentPostCode || '',
            agentCountry:
              countryList.find(
                (country) =>
                  country.value === companyData.rtiSetting.agentCountry,
              ) || null,
          }
        : {};
      form.setFieldsValue(initialValues);
      setNeedReinitializeForm(false);
    }
  }, [companyData?.rtiSetting]);

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      {/* RTI Settings */}
      <div className="mb-6">
        <Form.Item
          name="enableRTI"
          valuePropName="checked"
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}
        >
          <AppCheckbox>
            {messages['company.rti.enableRTI'] || 'Enable RTI?'}
          </AppCheckbox>
        </Form.Item>
        {enableRTI && (
          <Row gutter={24}>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.username']}
                name="userId"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.password']}
                name="password"
              >
                <AppInputPassword placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </div>

      {/* Agent Details */}
      <div className="mb-6">
        <div className="text-[14px] font-bold mb-[12px]">
          {messages['company.rti.agentDetails']}
        </div>
        <Row gutter={24}>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.forename']} name="agentForename">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.surname']} name="agentSurname">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.address1']} name="agentAddress1">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.address2']} name="agentAddress2">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.cityTown'] || 'City/Town'}
              name="agentCity"
            >
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.county']} name="agentCounty">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.postCode']} name="agentPostCode">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.country']} name="agentCountry">
              <AppSelect
                placeholder={messages['form.selectCountry']}
                options={countryList}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </AppFormContainer>
  );
};

export default RTISettingsForm;
